<script setup lang="ts">
  import ProfileSection from './ProfileSection.vue'
  import DeleteDialog from '@/components/dialog/DeleteDialog.vue'
  import GIPoint from '@/components/projectDetail/GIDrawer/GIPoint.vue'
  import type { SectionLine, SectionLineUploadCollection } from '@/stores/type/sectionLine.type'
  import ProfileCollection from '@/components/projectDetail/ProfileDrawer/ProfileCollection.vue'
  import ProfileActions from '@/components/projectDetail/ProfileDrawer/ProfileActions.vue'
  import ProfileCollectionGraphView from '@/components/projectDetail/ProfileDrawer/ProfileCollectionGraphView.vue'
  import PolygonShapeFileUploadDialog from '@/components/shapeFileUpload/PolygonShapeFileUploadDialog.vue'
  import useProfilePermissions from '@/composables/useProfilePermission'
  import NoDataAvailableText from '@/components/projectDetail/components/NoDataAvailableText.vue'

  const projectStore = useProjectStore()
  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()
  const giPointsStore = useGIPointsStore()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const { canAddSectionLine } = useProfilePermissions()

  const { isLengthProfileGraphBarShown } = storeToRefs(projectStore)
  const { filteredGIPoint, selectedItems: selectedGIPoints } = storeToRefs(giPointsStore)
  const {
    currentItem,
    items: sectionLineUploadCollections,
    selectedItems: selectedSectionLineCollections
  } = storeToRefs(sectionLineUploadCollectionsStore)

  const {
    deleteDialog,
    showSelectMap,
    selectedItems: selectedSectionLines,
    isSelectedAll
  } = storeToRefs(sectionLinesStore)

  const { deleteItem: deleteSelection, giPointsBySectionLineId } =
    storeToRefs(sectionLinesGIPointsStore)

  const headers = [
    { title: 'Name', key: 'properties.location_id' },
    { title: 'Survey Company', key: 'properties.survey_company' },
    { title: 'Survey Date', key: 'properties.survey_datestring' },
    { title: 'GI Type', key: 'properties.gi_type' },
    { title: 'End Depth', key: 'properties.end_depth' },
    { title: 'Z Coordinates', key: 'geometry.coordinates' }
  ]
  const sortedSectionLines = (item: SectionLineUploadCollection) => {
    return item.sectionline_set.features.sort((a: SectionLine, b: SectionLine) => a.id - b.id)
  }

  const removeGIPointSelections = async (sectionLine: SectionLine) => {
    await sectionLinesGIPointsStore.removeGIPointsFromSectionLine(
      selectedGIPoints.value,
      sectionLine.id
    )
    sectionLinesStore.toggleSelectionOption(sectionLine.id, null)
  }

  const openedIds = ref<number[]>([currentItem.value?.id])
  watch(currentItem, () => {
    openedIds.value.push(currentItem.value?.id)
  })

  watch(deleteDialog, () => {
    if (!deleteDialog.value) {
      sectionLineUploadCollectionsStore.setDeleteItem({})
    }
  })
</script>

<template>
  <ProfileCollectionGraphView v-if="isLengthProfileGraphBarShown" />

  <v-list
    v-else
    density="compact"
    class="pt-0 overflow-y-auto"
    v-model:opened="openedIds"
    style="max-height: 91%"
  >
    <ProfileActions />
    <p v-if="canAddSectionLine" class="my-2 ml-3 text-subtitle-2">
      To draw a section line, please select a dataset.
    </p>
    <div class="d-flex align-center" style="height: 40px">
      <v-checkbox
        :model-value="isSelectedAll"
        @update:model-value="
          sectionLineUploadCollectionsStore.selectedItems.length ===
          sectionLineUploadCollections.length
            ? sectionLineUploadCollectionsStore.clearSelectedSectionLineCollections()
            : sectionLineUploadCollectionsStore.selectAllSectionLineCollections()
        "
        :disabled="!sectionLineUploadCollections.length"
        color="primary"
        hide-details
        class="px-1"
      ></v-checkbox>
      <span>Select all</span>
    </div>
    <div>
      <v-list-group
        v-for="item in sectionLineUploadCollections"
        :key="item.id"
        :value="item.id"
        subgroup
      >
        <template v-slot:activator="{ props }">
          <v-list-item
            :nav="true"
            density="compact"
            :v-ripple="true"
            class="w-100 py-0 mb-0 mt-0 list-item rounded-0"
            v-bind="props"
            :class="{ 'selected-item': selectedSectionLineCollections.includes(item) }"
          >
            <ProfileCollection
              class="my-1"
              :store="sectionLineUploadCollectionsStore"
              :item="item"
              @delete="sectionLineUploadCollectionsStore.toggleDeleteDialog(item)"
            />
            <PolygonShapeFileUploadDialog :collection="item" />
          </v-list-item>
        </template>
        <v-virtual-scroll :items="sortedSectionLines(item)" :max-height="900">
          <template v-slot:default="{ item: profile }">
            <v-list-group :v-ripple="true" class="sub-group-item w-100" subgroup>
              <template v-slot:activator="{ props }">
                <v-list-item
                  :nav="true"
                  class="ma-0 py-0 pl-2 list-item section-item rounded-0"
                  density="compact"
                  v-bind="props"
                  :class="{ 'selected-item': selectedSectionLines.includes(profile) }"
                >
                  <ProfileSection
                    class="pl-2 my-2"
                    :item="profile"
                    @delete="sectionLinesStore.toggleDeleteDialog(profile)"
                    @remove-gi-point-selection="removeGIPointSelections(profile)"
                    @cancel-selection="
                      () => {
                        sectionLinesStore.toggleSelectionOption(item.id, null)
                        giPointsStore.setSelectedItems([])
                      }
                    "
                  />
                </v-list-item>
              </template>
              <v-data-table
                :show-select="showSelectMap[profile.id] === 'remove'"
                :class="{ 'selected-item': selectedSectionLines.includes(profile) }"
                v-model="selectedGIPoints"
                :items="giPointsBySectionLineId(profile.id)"
                item-value="id"
                :headers="headers"
                items-per-page="-1"
                :hover="true"
                no-data-text="No GI Points available."
                class="row-pointer"
                density="compact"
              >
                <template v-slot:item="{ item }">
                  <GIPoint
                    :item="item"
                    :selectedItems="selectedGIPoints"
                    :show-select="showSelectMap[profile.id] === 'remove'"
                    @update:selectedItems="(val) => giPointsStore.addSelectedGIPoint(val)"
                    @click="giPointsStore.setCurrentItem(item)"
                  />
                </template>
              </v-data-table>
            </v-list-group>
          </template>
        </v-virtual-scroll>

        <template v-if="!sortedSectionLines(item).length">
          <v-list-item density="compact" class="ml-5">
            <small>No Data available.</small>
          </v-list-item>
        </template>
      </v-list-group>
    </div>
    <NoDataAvailableText v-if="!sectionLineUploadCollections.length">
      There are no section line datasets available.
    </NoDataAvailableText>
  </v-list>

  <DeleteDialog
    :store="sectionLinesGIPointsStore"
    :name="filteredGIPoint(deleteSelection?.gi_point)"
  ></DeleteDialog>
</template>

<style scoped>
  div :deep(.v-list-item__prepend) {
    max-width: 24px;
    margin-left: 4px;
  }

  .section-item :deep(.v-list-item__prepend) {
    margin-left: 10px;
  }

  :deep(.v-data-table-footer) {
    display: none;
  }

  :deep(.v-list .v-list-item--nav:not(:only-child)) {
    margin-bottom: 0 !important;
  }

  :deep(.v-list-group__items .v-list-item) {
    /* overwrites vuetify's default padding-inline-start: calc(16px + var(--indent-padding))!important;*/
    padding-inline-start: 10px !important;
  }

  .sub-group-item :deep(.v-list-item__append) {
    padding-right: 8px;
  }

  .selected-item {
    background-color: #f4fcff;
  }

  .list-item:hover {
    background-color: #f4fcff;
  }

  :deep(.v-table__wrapper) table tbody tr:hover {
    background-color: #f4fcff;
  }
</style>
